// @flow
import { useQuery, QueryOptions } from 'react-query';
import useAbortController from 'hooks/useAbortController';
import { useContext, useMemo } from 'react';
import { AppContext } from 'contexts/AppContext';
import { getProfileInfo } from 'services/userService';
import { getLocalAuth } from 'utils/auth';

type UserProfile = {
  portalUserId: string,
  firstName: string,
  lastName: string,
  preferredName: string,
  customerStatus: string,
  phoneNumber: string,
  emailAddress: string,
  applicantId: string,
  customerId: string,
  isPrimaryProspect: boolean,
  isFinanciallyResponsible: boolean,
  areFeesApplied: false,
  areFeesPaid: false,
  prospectPreferences: {
    id: string,
    nBeds: null,
    nBedsArr: [],
    preferredFloorPlanId: string,
    nBaths: null,
    nHalfBaths: null,
    priceFrom: null,
    priceTo: null,
    leaseTermInMonths: null,
    moveInDateFrom: string,
    moveInDateTo: string,
    moveInDateScheduled: null,
    wants: null,
    notWants: null,
    helpText: null,
    hasUserChosenToProvideMoveInPreferences: null,
    createdAt: string,
    updatedAt: string,
    prospectId: string,
    unitId: string,
  },
};
const useUserProfileQuery = ({ options } = {}) => {
  const { organizationId, propertyId } = useContext(AppContext);
  const abortControllerOptions = useAbortController();
  const queryKey = ['getProfileInfo', organizationId, propertyId];

  const memoizedOptions: QueryOptions = useMemo(() => {
    return {
      enabled: !!organizationId && !!propertyId,
      placeholderData: {},
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnSettled: false,
      retry: false,
      ...options,
    };
  }, [options, organizationId, propertyId]);

  return useQuery(
    queryKey,
    async (): Promise<UserProfile> => {
      const { authUser } = getLocalAuth();

      return await getProfileInfo(
        {
          organizationId,
          propertyId,
          portalUserId: authUser.id,
        },
        abortControllerOptions,
      );
    },
    memoizedOptions,
  );
};

export default useUserProfileQuery;
